import { useEffect, useRef } from "react";
import "@/components/site/ondose-features.css";
import "@/lib/ondose-features";

const Icon = ({ children, className = "odf-icon" }) => (
  <svg className={className} viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);

const Small = ({ cls, icon, title, text, testid }) => (
  <article className={`odf-card odf-small ${cls}`} data-testid={testid}>
    <Icon>{icon}</Icon>
    <div><h3>{title}</h3><p>{text}</p></div>
  </article>
);

export default function KeyFeatures() {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    window.initOnDoseFeatures();
    return () => root.odfCleanup?.();
  }, []);
  return (
    <section id="features" className="odf" aria-labelledby="odf-title" ref={ref} data-testid="section-features">
      <div className="odf-wrap">
        <header className="odf-intro">
          <p className="odf-eyebrow">KEY FEATURES</p>
          <h2 id="odf-title">Built for busy counters<br className="odf-break" /> and every kind of patient.</h2>
          <p className="odf-description">Every patient you send home with medicines can get a reminder call: young or old, first visit or long-term care.</p>
          <p className="odf-highlight" data-testid="features-simple-line">One reminder call at each medicine time. Just answer and listen. No app needed.</p>
        </header>
        <div className="odf-grid">
          <article className="odf-card odf-featured" data-testid="feature-merged">
            <h3>One call, several doctors</h3>
            <p>Medicines due at the same time? One reminder call, with each doctor and hospital named.</p>
            <div className="odf-merge" role="img" aria-label="Sample: reminders from Dr. Anil, Dr. Ramesh and Dr. Kavitha, scheduled for the same time, merge into one call at 8 AM.">
              <div className="odf-doctors"><span className="odf-sample">Samples</span><span>Dr. Anil · Thyroid</span><span>Dr. Ramesh · BP</span><span>Dr. Kavitha · Sugar</span></div>
              <svg className="odf-connect" viewBox="0 0 180 120" preserveAspectRatio="none" fill="none" aria-hidden="true"><path d="M0 15H35C85 15 75 60 120 60H175M0 60H175M0 105H35C85 105 75 60 120 60M164 50l11 10-11 10" /></svg>
              <span className="odf-mobile-arrow" aria-hidden="true">↓</span>
              <div className="odf-call"><Icon><path d="m8 4 4 6-3 3c2 4 5 7 9 9l3-3 6 4c-1 5-4 6-8 4C10 23 5 18 3 10 2 6 4 4 8 4Z" /></Icon><strong>1 call · 8:00 AM</strong></div>
            </div>
          </article>
          <article className="odf-card odf-language" data-testid="feature-language">
            <Icon><rect x="2" y="9" width="12" height="20" rx="3" /><path d="M6 24h4M7 12h2M19 3h10v12h-6l-4 4V3ZM22 7h4m-2-2v6" /></Icon>
            <h3>In their own language,<br />on any phone</h3>
            <p>Telugu now; Hindi, Tamil and Kannada coming. No smartphone needed.</p>
            <span className="odf-pill">No app to install</span>
          </article>
          <Small cls="odf-hospital" testid="feature-hospital" title="Your hospital and doctor in every call" text="Patients hear who cares for them, every day."
            icon={<path d="M9 29V4h14v25M9 14H3v15h26V14h-6M13 10h6m-3-3v6M13 29v-7h6v7M5 19h1m20 0h1" />} />
          <Small cls="odf-scan" testid="feature-scan" title="Prescription scan" text="Staff check and confirm every medicine before any call."
            icon={<path d="M2 9V3h6m16 0h6v6M2 23v6h6m16 0h6v-6M10 7h8l5 5v13H10V7Zm8 0v6h5M13 17h7m-7 4h5" />} />
          <Small cls="odf-refill" testid="feature-refill" title="Refill & follow-up reminders" text="A reminder to return before medicines run out."
            icon={<><rect x="3" y="6" width="26" height="23" rx="3" /><path d="M3 13h26M10 3v7m12-7v7M9 19h4m6 0h4M9 24h4" /></>} />
          <article className="odf-card odf-report" data-testid="feature-report">
            <div className="odf-report-heading">
              <Icon><path d="M6 3h14l6 6v20H6V3Zm14 0v7h6M11 15h10M11 20v5m5-8v8m5-3v3" /></Icon>
              <div><h3>Delivery tracking &amp; reports</h3><p>See who was reached and which refills are due.</p></div>
            </div>
            <div className="odf-report-panel">
              <table><caption>Sample report · Illustrative data</caption><tbody>
                <tr><th scope="row">Reminders delivered</th><td>58 / 60</td></tr>
                <tr><th scope="row">Refills due this week</th><td>12</td></tr>
              </tbody></table>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
